import type { HTMLAttributes, ReactNode } from 'react'
import { cn } from '../lib/cn'

/**
 * Miniatura com legenda: a foto num quadrado de fio, a legenda abaixo em rótulo e uma ação em
 * texto à direita da legenda ("arquivar"). Em grade (`PhotoGrid`): 3 por linha no celular, 4 no
 * iPad e 6 no desktop. Enquanto a foto sobe, a miniatura fica em alto com o texto do estado no
 * lugar da imagem; quando falha, o fio vira plum e a legenda diz a causa.
 */
export interface PhotoTileProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  /** O endereço da miniatura; sem ele, o quadrado mostra `placeholder`. */
  src?: string | null
  alt: string
  /** O que está escrito embaixo ("14:20 · Camila"). */
  caption?: ReactNode
  /** Ação em texto ao lado da legenda ("arquivar", "trocar"). */
  action?: ReactNode
  /** O que mostrar no quadrado sem imagem ("Enviando…", "Não enviada"). */
  placeholder?: ReactNode
  /** Estado da miniatura: `uploading` em alto animado; `failed` com fio plum. */
  state?: 'ready' | 'uploading' | 'failed'
  /** Abre a foto no tamanho real quando existe. */
  href?: string
}

export function PhotoTile({
  src,
  alt,
  caption,
  action,
  placeholder,
  state = 'ready',
  href,
  className,
  ...props
}: PhotoTileProps) {
  const image = src ? (
    <img src={src} alt={alt} loading="lazy" className="block size-full object-cover" />
  ) : (
    <span
      role="img"
      aria-label={alt}
      className={cn(
        'font-body text-eyebrow text-label flex size-full items-center justify-center px-2 text-center font-light tracking-normal',
        state === 'failed' && 'text-plum',
      )}
    >
      {placeholder}
    </span>
  )
  return (
    <figure
      data-slot="photo-tile"
      data-state={state}
      className={cn('flex min-w-0 flex-col gap-[6px]', className)}
      {...props}
    >
      <div
        className={cn(
          'bg-pampas aspect-square w-full overflow-hidden border',
          state === 'failed' ? 'border-plum' : 'border-hairline',
          state === 'uploading' && 'bg-alto/60 animate-pulse',
        )}
      >
        {href && src ? (
          <a href={href} target="_blank" rel="noopener" className="block size-full">
            {image}
          </a>
        ) : (
          image
        )}
      </div>
      {caption || action ? (
        <figcaption className="font-body text-eyebrow text-label flex items-baseline justify-between gap-2 font-light tracking-normal">
          <span className={cn('min-w-0 truncate', state === 'failed' && 'text-plum')}>
            {caption}
          </span>
          {action ? <span className="shrink-0">{action}</span> : null}
        </figcaption>
      ) : null}
    </figure>
  )
}

/** A grade das miniaturas: 3 · 4 · 6 por linha conforme a largura. */
export function PhotoGrid({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      data-slot="photo-grid"
      className={cn('tablet:grid-cols-4 desk:grid-cols-6 grid grid-cols-3 gap-3', className)}
      {...props}
    />
  )
}
